/**
 * spiceVisitPicker - pure helpers for the Email Helper's SPICE client
 * picker (shown in SPICE mode whenever no client id is in the URL): the
 * search-by-name filter shared by the therapist and office lists, and the
 * lookup that recovers a chosen client's last plan from My week, the only
 * place on this page a decrypted plan line already lives.
 */

import type { CmHomeMyWeekWire } from '@/lib/cmHomeApi';

export interface SpiceClientPickerRow {
  id: string;
  display_name: string;
  /**
   * The client's emergency contact's or POA's first name, when the
   * row's own source already carries it decrypted. Both of today's
   * sources (the home snapshot's SPICE facts, the office's SPICE
   * roster) are structural-only and decrypt nothing — always null from
   * either today (each says so at its own call site) — but the picker's
   * row shape carries the field so a future decrypted source has
   * somewhere to put it without a second onPick signature change.
   */
  contact_first_name: string | null;
}

/** Case-insensitive substring match on display_name. A blank query
 *  returns every row, unfiltered. Bound to just the two fields it reads
 *  (Pick, not the full row) so a caller's own wire type — which may not
 *  carry contact_first_name at all — needs no reshaping to call this. */
export function filterSpiceClientsByName<T extends Pick<SpiceClientPickerRow, 'id' | 'display_name'>>(
  rows: readonly T[],
  query: string,
): T[] {
  const q = query.trim().toLowerCase();
  if (q === '') return [...rows];
  return rows.filter((r) => r.display_name.toLowerCase().includes(q));
}

/** The chosen client's last plan, read from My week (the viewer's own
 *  scheduled visits this week) - the only source of decrypted plan text
 *  already on this page. null when the client has no visit in this
 *  week's grid (a cover client with nothing scheduled, or no plan on
 *  file yet), or when My week isn't present at all (the office path). */
export function lastPlanFromMyWeek(
  myWeek: CmHomeMyWeekWire | null,
  clientId: string,
): string | null {
  if (myWeek === null) return null;
  for (const day of myWeek.days) {
    for (const visit of day.visits) {
      if (visit.client_id === clientId) return visit.last_plan;
    }
  }
  return null;
}

/** A client's display name, read from whichever row list is on hand (the
 *  office roster or a therapist's own caseload) - the page's fallback
 *  source for naming a client that arrived via a direct URL rather than
 *  a door or a picker pick. null when the id isn't in the list (still
 *  loading, or a client outside this viewer's reach). */
export function displayNameFromRows<T extends Pick<SpiceClientPickerRow, 'id' | 'display_name'>>(
  rows: readonly T[],
  clientId: string,
): string | null {
  return rows.find((r) => r.id === clientId)?.display_name ?? null;
}
